
import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronLeft } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Shimmer } from "@/components/ui/shimmer";

const BecomeCook = () => {
  const navigate = useNavigate();

  // Query to check existing verification status
  const { data: verification, isLoading } = useQuery({
    queryKey: ['cook-verification-status'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('cook_verifications')
        .select('*')
        .eq('cook_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    }
  });

  // Effect to handle automatic routing based on verification status
  useEffect(() => {
    if (!isLoading && verification) {
      // If they have uploaded documents but haven't completed the quiz
      if (verification.government_id_url && !verification.quiz_completed_at) {
        navigate('/seller/verification/quiz');
        return;
      }
      
      // If they have started but not completed document uploads
      if (verification.status === 'in_progress') {
        navigate('/seller/verification/documents');
        return;
      }

      // If their application is pending review
      if (verification.status === 'pending') {
        navigate('/seller/verification/documents');
        return;
      }

      // If they've been rejected, let them start over at profile
      if (verification.status === 'rejected') {
        navigate('/seller/verification/profile');
        return;
      }
    }
  }, [verification, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <main className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto p-6">
            <Shimmer className="w-full h-32" />
          </Card>
        </main>
      </div>
    );
  }

  const handleGetStarted = () => {
    navigate("/seller/verification/profile");
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-8">
        <Card className="max-w-2xl mx-auto p-6">
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate('/profile')}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="space-y-6 text-center">
            <h2 className="text-2xl font-semibold tracking-tight text-primary">
              Become a Chef
            </h2>
            <p className="text-foreground">
              The process to become a Chef involves three steps: your profile creation, 
              identity and cleanliness verification, and a food safety quiz. Click the 
              button below to get started!
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              By becoming a Chef, you agree to the <Link to="/chef-terms" className="text-primary hover:underline">Chef Terms and Conditions</Link>.
            </p>
            <Button 
              className="w-full md:w-auto"
              onClick={handleGetStarted}
            >
              Get Started
            </Button>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default BecomeCook;
